<template>
    <div id="images" class="media-slides">
        <vue-easy-lightbox
            v-if="showLightbox"
            :visible="visible"
            :imgs="imgs"
            :index="selectedIndex"
            @hide="handleHide"
            @hook:updated="addA11y"
        >
        </vue-easy-lightbox>
        <div class="hero-container">
        <figure v-if="hero" class="hero-media">
            <img :src="getHeroUrl(hero)" :alt="alt">
            <div class="buttons">
                <a class="download-btn" :href="downloadUrl(hero)" target="_blank" :download="downloadTitle(hero)">
                    <span class="sr-only">
                        {{ $t('labels.download_image') }}
                    </span>
                </a>
                <button class="expand-btn" @click="showMultiple($event)">
                    <span class="sr-only">
                        {{ $t('labels.enlarge_image') }}
                    </span>
                </button>
                <button class="info-btn" @click="toggleInfo($event)" 
                        :aria-pressed="showInfo"
                        aria-controls="info">
                    <span aria-hidden="true">i</span>
                    <span class="sr-only">
                        {{ $t('labels.get_image_info') }}
                    </span>
                </button>
                <div id="info" class="copy-title" v-if="showInfo">
                    <p>{{ imgTitle(hero) }}</p>
                </div>
            </div>
        </figure>
        </div>

        <ul v-if="images.length">
            <li v-for="(m, i) in images" :key="i" @click="changeHero(m, i)">
                <figure :class="{'current-hero': isHero(m)}">
                    <img :src="getThumbUrl(m)" :alt="alt" :title="imgTitle(m)">
                </figure>
            </li>
        </ul>
    </div>
</template>
<script>
import { mapGetters } from 'vuex'

export default {
    name: "ImageViewer",
    data: function () {
        return {
            imgs: '',
            visible: true,
            selectedIndex: 0,
            showLightbox: false,
            showInfo: false,
            addListeners: true,
        }
    },
    computed: {
        ...mapGetters({
            'hero': 'detail/hero',
            'loaded': 'detail/item_loaded',
            'loading': 'detail/item_loading'
        })
    },
    props: {
        'images': Array,
        'alt': String
    },
    methods: {
        downloadTitle (media) {
          if (media.sizes) {
              return `${media.sizes.full.filename}`;
          } else if (media.filename) {
              return media.filename;
          }
        },
        downloadUrl (media) {
          if (media.sizes) {
              return `${media.sizes.full.url}`;
          } else if (media.url) {
              return media.url;
          }
        },
        imgTitle (media) {
            return `${media.title}, ${media.filename}`;
        },
        getHeroUrl (media) {
          if (media.sizes) {
              return media.sizes.large.url;
          } else if (media.url) {
              return media.url;
          }
        },
        getThumbUrl (media) {
          if (media.sizes) {
              return media.sizes.small.url;
          } else if (media.url) {
              return media.url;
          }
        },
        isHero(media) {
            if (!this.hero) return;
            return this.hero.id == media.id;
        },
        changeHero(media, index) {
            if (!this.hero) {
                this.$store.dispatch('detail/hero', media)
                this.selectedIndex = index;
            } else if (this.isHero(media)) {
                return;
            } else {
                this.$store.dispatch('detail/hero', media)
                this.selectedIndex = index;
            }
        },
        toggleInfo: function () {
            this.showInfo = !this.showInfo;
        },
        showMultiple: function (e) {
            switch(e.type) {
                case 'click':
                    this.show(this.selectedIndex);
                    break;
                case 'keydown':
                    if (e.key !== ' ' && e.key !== 'Enter') return;
                    if (e.key == ' ') e.preventDefault();
                    this.show(this.selectedIndex);
                    break;
                default:
                    return;
            }
        },
        getSelectId: function (index) {
            return `select-${index}`;
        },
        show: function (index) {
            this.showLightbox = true;

            // an array of image objects {src: 'url', title: 'caption'}
            this.imgs = this.images.map(i => {
                let url = i.sizes ? i.sizes.full.url : i.url;
                return {src: url, title: this.imgTitle(i) }
            }); 
            this.selectedIndex = index;
            this.visible = true;
            this.savedFocus = document.activeElement;

            setTimeout(function () {
                let btn = document.querySelector('.toolbar-btn__zoomin');
                btn.setAttribute('tabindex', -1);
                btn.focus();
                btn.setAttribute('tabindex', 0);
            });
        },
        addA11y: function () {
            // add event listeners to buttons
            let buttons = document.querySelectorAll('.vel-btns-wrapper [class*="btn"]');
            var lightbox = this;

            if (this.addListeners) {
                this.restrictLightboxTabs();
                this.listenForArrows();
            }

            buttons.forEach((btn, index, buttons) => {
                btn.setAttribute('role', 'button');
                btn.setAttribute('tabindex', 0);
                btn.setAttribute('aria-label', this.getLabel(btn, index, buttons));

                if (!btn.getAttribute('hasListeners')) {
                    btn.addEventListener('keydown', function (e) {
                        if (e.key == ' ' || e.key == 'Enter') {
                            e.preventDefault();
                            this.click();
                        }
                    });

                    if (btn.classList.contains('toolbar-btn__zoomin') || btn.classList.contains('toolbar-btn__zoomout')) {
                        btn.addEventListener('click', function () {
                            this.click();
                            this.click();
                        });
                    }

                    if (btn.classList.contains('btn__next') || btn.classList.contains('btn__prev')) {
                        btn.addEventListener('click', function () {
                            if (this.classList.contains('btn__next') && lightbox.selectedIndex < lightbox.imgs.length - 1) {
                                lightbox.selectedIndex = lightbox.selectedIndex + 1;
                            } else if (this.classList.contains('btn__prev') && lightbox.selectedIndex > 0 ) {
                                lightbox.selectedIndex = lightbox.selectedIndex - 1;
                            }
                        });
                    }
                }

                btn.setAttribute('hasListeners', true);

                // add aria attributes
                if (document.querySelector('.vel-img-title')) {
                    document.querySelector('.vel-img-title').id = 'img-title';
                }

                setTimeout(function () {
                    if (document.querySelector('.vel-img')) {
                        document.querySelector('.vel-img').setAttribute('aria-describedby', 'img-title');
                    }
                }, 800)

                document.querySelector('.vel-img-modal').setAttribute('aria-live', 'polite');
                document.querySelector('.vel-img-modal').setAttribute('aria-modal', 'true');
            });

            lightbox.addListeners = false;
        },
        getLabel: function (btn, index, buttons) {
            let translatedLabels = this.$i18n.messages[this.$i18n.locale].labels;
            var labels = {
                btn__prev: translatedLabels['lightbox_prev'],
                btn__next: translatedLabels['lightbox_next'],
                btn__close: translatedLabels['lightbox_close'],
                toolbar_btn__zoomin: translatedLabels['lightbox_zoomin'],
                toolbar_btn__zoomout: translatedLabels['lightbox_zoomout'],
                toolbar_btn__resize: translatedLabels['lightbox_resize'],
                toolbar_btn__rotate: translatedLabels['lightbox_rotate_left'],
                toolbar_btn__rotate_right: translatedLabels['lightbox_rotate_right'],
            };
            var label = '';

            if (index < buttons.length - 1) {
                btn.classList.forEach(cls => {
                    if (cls.includes('btn__')) {
                        label = labels[cls.replace('-', '_')];
                    }
                });
            } else {
                // handle case where both rotate buttons have same class
                btn.setAttribute('data-rotate', 'right');
                label = labels['toolbar_btn__rotate_right'];
            }

            return label;
        },
        listenForArrows: function () {
            //  listen for arrow keypress
            let lightbox = this;
            let btnWrapper = document.querySelector('#images');
            btnWrapper.addEventListener('keydown', e => {
                if (e.key == 'ArrowRight' && (lightbox.selectedIndex < lightbox.imgs.length - 1)) {
                    lightbox.selectedIndex = lightbox.selectedIndex + 1;
                } else if (e.key == 'ArrowLeft' && lightbox.selectedIndex > 0 ) {
                    lightbox.selectedIndex = lightbox.selectedIndex - 1;
                }
            });
        },
        restrictLightboxTabs: function () {
            let btnWrapper = document.querySelector('#images');
            let lightbox = this;
            let keysPressed = {};

            btnWrapper.addEventListener('keydown', function (e) {
                keysPressed[e.key] = true;

                let firstButton = e.target.classList.contains('btn__prev');
                let lastButton = e.target.getAttribute('data-rotate') == 'right';

                if (lightbox.images.length == 1) {
                    firstButton = e.target.classList.contains('btn__close');
                }

                if (firstButton) {
                    if (keysPressed['Tab'] && keysPressed['Shift']) {
                        e.preventDefault();
                    }
                } else if (lastButton) {
                    if (keysPressed['Tab'] && !keysPressed['Shift']) {
                        e.preventDefault();
                    }
                }
            });

            btnWrapper.addEventListener('keyup', e => {
                if (keysPressed[e.key]) keysPressed[e.key] = false;
            });
        },
        handleHide: function () {
            this.savedFocus.focus();
            this.visible = false;
        }
    }
}
</script>

<style lang="scss">

.media-slides {
    margin: 0 auto;
    max-width: 900px;
    // max-height: 550px;
    display: flex;
    justify-content: center;

    ul {        
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        max-height: 550px;
        justify-content: flex-start;
        padding: $s1 0;
        margin-left: $s3;

        li {
            width: calc(min(33%, 120px));
            padding: .5rem;
        }
    }
    
    figure {
        margin: 0;
        width:100%; 

        &.current-hero {
            img {
                outline: solid 4px $cwm-green;
            }
        }

        img {
            max-width: 450px;
            height: auto;
            width: 100%;
        }
    }

    .hero-container {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .hero-media {
        width: 50%;
        min-width: 300px;
        // max-height: 550px;
        position: relative;
        height: fit-content;
       
        img {
            object-fit: contain;
            height: auto;
            width: 100%;
        }

        .buttons {
            position: absolute;
            bottom: 3px;
            left: 0;
            display: flex;

            button, .download-btn {
                display: inline-block;
                height: 40px;
                width: 40px;
                box-sizing: border-box;
                background: $dark-gray1;
                border: none;
                margin-right: 4px;
                position: relative;
                cursor: pointer;
            }

            .download-btn:after {
                content: url('../assets/images/icon-download.svg');
                height: 40px;
                width: 40px;
                display: flex;
                justify-content: center;
                align-items: center;
            }

            .expand-btn:after {
                content: url('../assets/images/icon-full-screen.svg');
                display: flex;
                justify-content: center;
                align-items: center;
            }

            .info-btn {
                color: $med-gray1;
                font-size: 1.125rem;
            }

            .copy-title {
                position: absolute;
                background: $dark-gray1;
                color: $white;
                width: fit-content;
                bottom: 44px;
                padding: 12px;
                left: 88px;

                p {
                    margin: 0;
                    min-width: 280px;
                    font-size: .8rem;
                    line-height: .95rem;
                }
            }
        }
    }
}


#images {
    .vel-img-modal.vel-modal {
        background-color:  rgba(0,0,0,.88);

        img.vel-img {
            width: auto;
            margin-top: -140px;
            transform: scale(.8);
        }

        .vel-img-title {
            overflow: unset;
            white-space: unset;
            bottom: 25px;
            max-width: 60%;
            line-height: 1.2rem;
        }

        .vel-btns-wrapper {
            
            [class*="btn"]:focus {
                outline: #92b2cf solid 1px;
            }

            .btn__close,
            .btn__next,
            .btn__prev {
                opacity: 1;
                font-size: 45px;
                padding: 5px;
                background:  rgba(0,0,0,.5);

                &.disable {
                    opacity: .3;
                }
            }

            .btn__next { right: 10%; }
            .btn__prev { left: 10%; }
            .btn__close {
                font-size: 30px;
                top: 8%;
                right: 5%;
            }

            .vel-toolbar {
                border-radius: 0;
                bottom: 80px;
                opacity: 1;
                font-size: 22px;
                overflow: visible;
                background-color: transparent;

                .toobar-btn {
                    background-color: rgba(0,0,0,.5);
                    padding: 10px 22px;
                    margin: 0 5px;
                    svg {
                        color: #C0C0C0;
                        &:hover { color: white; }
                    }
                    &:first-child { margin-left: 0;}
                    &:last-child { margin-right: 0;}
                }
            }
        }

    }

}


@media screen and (max-width: $mq-max-width-desktop) {
    .media-slides {
        max-width: 1200px;
        justify-content: center;

        .hero-container {
            justify-content: center;
        }

        .hero-media {
            width: 60%;
            img {
                display: block;
            }
            .buttons {
                bottom: 0;
            }
        }
        ul {
            padding-top: 0;
            margin-left: $s2;
            li {
                width: 85px;
            }
        }
    }
}

@media screen and (max-width: $mq-max-width-tablet) {
    #images {
        max-width: initial;
        justify-content: unset;

        .hero-media {
            padding-left: 20px;
        }
        ul {
            li {
                width: 75px;
            }
        }
    }
}

@media screen and (max-width: $mq-max-width-mobile) {
    #images {
        flex-direction: column;
        align-items: center;
        padding-left: $side-padding-mobile;
        padding-right: $side-padding-mobile;

        .hero-media {
            padding: 0;
        }
        ul {
            width: 100%;
            flex-direction: row;
            min-height: unset;
            align-items: center;
            justify-content: center;
            margin-left: 0;
        }
    }
}
</style>